'use client'

import { useRef, useState, useEffect } from 'react'
import { clsx } from 'clsx'
import { onResizeEnd } from '@/utils/common'
import { getImageUrl } from '@/utils/image/getImageUrl'
import { imageSizes } from '@/utils/image/imageSizes'
import styles from './Image.module.scss'
import NextImage from 'next/image'
import { getAppConfig } from '@/config/config'
import { slugify } from '@/utils/string'

export type ImageProps = {
  src?: string
  image?: {
    heightRatio: number
    filename: string
    baseUrl: string
    crop: string
    originalWidth?: number
    originalHeight?: number
  }
  isOriginalSize?: boolean
  alt: string
  priority?: boolean
  defaultWidth?: number
  defaultHeight?: number
  fallbackImage?: string
  className?: string
  noCrop?: boolean
  cropOverride?: string
}

const Image = ({
  image,
  src,
  isOriginalSize = false,
  noCrop = false,
  cropOverride,
  alt,
  priority = false,
  defaultWidth = 1024,
  defaultHeight = 576,
  fallbackImage = `${getAppConfig().basePath}/assets/logos/logo_grey.svg`,
  className,
}: ImageProps) => {
  const fadeIn = (element: HTMLImageElement) => {
    element.style.opacity = '1'
    element.style.display = 'block'
  }

  const imgElem = useRef<HTMLImageElement | null>(null)
  const [imgWidth, setImgWidth] = useState<number | undefined>(
    defaultWidth ?? (priority ? imageSizes.at(-1) : undefined),
  )
  const imgWidthRef = useRef<number | undefined>(imgWidth)

  const crop = cropOverride ?? image?.crop ?? ''

  // Automatically force original size mode for freecrop
  const resolvedIsOriginal = crop === 'freecrop' ? true : isOriginalSize

  if (src && new URL(src).host === 'www.n-tv.de') {
    src = new URL(src).pathname
  }

  const imgUrl = src
    ? src
    : image?.baseUrl && imgWidth
      ? new URL(
          getImageUrl(image.baseUrl, crop, slugify(image?.filename), imgWidth, resolvedIsOriginal ?? false, 'webp', {
            originalWidth: image.originalWidth,
            originalHeight: image.originalHeight,
          }),
        ).pathname
      : fallbackImage

  imgWidthRef.current = imgWidth

  useEffect(() => {
    if (!image || priority || defaultWidth) return

    const updateWidth = () => {
      if (imgElem.current && imgElem.current.width) {
        const renderedWidth = imgElem.current.width
        const closestWidth = imageSizes.find((size) => renderedWidth <= size)

        if (closestWidth && (!imgWidthRef.current || closestWidth > imgWidthRef.current)) {
          setImgWidth(closestWidth)
        }
      }
    }

    updateWidth()
    onResizeEnd(updateWidth)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <NextImage
      ref={imgElem}
      className={clsx(styles.img, image?.crop && !noCrop ? styles[crop] : undefined, !imgUrl && styles.hide, className)}
      onError={({ currentTarget }) => {
        currentTarget.onerror = null
        if (fallbackImage && currentTarget.src !== fallbackImage) {
          currentTarget.src = fallbackImage
          currentTarget.srcset = fallbackImage

          fadeIn(currentTarget)
        }
      }}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : undefined}
      src={'image.jpg'}
      loader={() => (imgUrl ? imgUrl : fallbackImage ? fallbackImage : '')}
      alt={alt}
      width={defaultWidth}
      height={defaultHeight}
      onLoad={(event) => {
        fadeIn(event.currentTarget)
      }}
    />
  )
}

export default Image
